import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Phone, Check, Star, Search, ClipboardList, Car } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import RevealText from "@/components/shared/RevealText";
import Reveal from "@/components/shared/Reveal";
import SearchWidget from "@/components/shared/SearchWidget";
import ProviderStrip from "@/components/shared/ProviderStrip";
import FAQAccordion from "@/components/shared/FAQAccordion";
import CategoryCard from "@/components/shared/CategoryCard";
import CategoryGridCarousel from "@/components/shared/CategoryGridCarousel";
import TermsHint from "@/components/shared/TermsHint";
import CityCard from "@/components/shared/CityCard";
import CityGridCarousel from "@/components/shared/CityGridCarousel";
import { CATEGORIES, CITIES, FAQS_GENERAL, BRAND } from "@/lib/data";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.15]);

  return (
    <PageShell>
      {/* HERO — Navy background with car hero image */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-[#0B2545] text-white"
        data-testid="hero-section"
      >
        <motion.div style={{ y, opacity }} className="absolute inset-0 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=2200&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/85 to-[#0B2545]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545] via-transparent to-transparent" />
        </motion.div>

        <div className="relative mx-auto max-w-[1600px] px-5 md:px-10 pt-8 md:pt-12 pb-4 md:pb-12 flex flex-col">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="hidden md:flex overline-light mb-5 items-center gap-3 order-1"
          >
            <span className="inline-block h-1.5 w-1.5 bg-white rounded-full" />
            US car rental aggregator · Serving all 50 states
          </motion.div>

          <div className="order-2">
            <RevealText
              text={"Compare car rentals\nfrom every major US brand."}
              className="font-serif text-[9vw] md:text-[5.5vw] lg:text-[4.4vw] leading-[1.02] tracking-[-0.02em] text-white font-medium max-w-6xl"
              delay={0.15}
              stagger={0.12}
            />
          </div>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-5 md:mt-6 max-w-2xl text-white/85 text-base md:text-lg leading-relaxed order-3"
          >
            One search. Every major provider. A quote in under a minute — free,
            with zero booking fees.
          </motion.p>

          {/* Search widget — mobile: above CTAs; desktop: below CTAs (keeps desktop layout intact) */}
          <div className="order-4 md:order-5 mt-6 md:mt-8">
            <SearchWidget />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-4 md:mt-6 order-5 md:order-4"
          >
            <div className="flex flex-row gap-3">
              <a
                href={BRAND.phoneHref}
                className="btn-inverse flex-1 md:flex-none justify-center whitespace-nowrap !py-2 md:!py-[0.9rem] text-sm md:text-base"
                data-testid="hero-call-cta"
              >
                <Phone className="h-4 w-4" strokeWidth={2} />
                <span className="hidden md:inline">Call {BRAND.phone}</span>
                <span className="md:hidden">Call</span>
                <ArrowRight className="h-4 w-4 hidden md:inline" strokeWidth={1.8} />
              </a>
              <div className="relative inline-flex flex-col group flex-1 md:flex-none">
                <Link
                  to="/contact"
                  className="btn-ghost-dark w-full md:w-auto justify-center whitespace-nowrap !py-2 md:!py-[0.9rem] text-sm md:text-base"
                  data-testid="hero-quote-cta"
                >
                  <span className="hidden md:inline">Get a free quote</span>
                  <span className="md:hidden">Get a quote</span>
                  <ArrowRight className="h-4 w-4 hidden md:inline" strokeWidth={1.8} />
                </Link>
                {/* Desktop tooltip only — the mobile disclaimer is rendered separately below to span both buttons on one line */}
                <TermsHint variant="dark" align="right" dataTestId="hero-quote-hint" hideMobile />
              </div>
            </div>
            {/* Mobile-only single-line disclaimer below both buttons */}
            <p
              className="md:hidden mt-2 text-[10.5px] leading-snug text-white/60 whitespace-nowrap"
              data-testid="hero-quote-hint-mobile"
            >
              By clicking, you agree to our{" "}
              <Link to="/terms-conditions" className="text-white/85 underline underline-offset-2">
                Terms &amp; Conditions
              </Link>
              .
            </p>
          </motion.div>
        </div>
      </section>

      <ProviderStrip />

      {/* How it works */}
      <section className="py-6 md:py-16 bg-gradient-to-b from-white to-[#F5F7FA] relative overflow-hidden" data-testid="how-section">
        {/* Decorative element */}
        <div className="absolute top-10 right-10 md:top-20 md:right-20 pointer-events-none opacity-[0.05] hidden md:block">
          <div className="font-serif italic text-[16rem] leading-none text-[#0B2545]">03</div>
        </div>

        <div className="mx-auto max-w-[1600px] px-5 md:px-10 relative">
          <div className="max-w-4xl mb-6 md:mb-12">
            <div className="overline mb-4 md:mb-5 flex items-center gap-3">
              <span className="h-px w-8 bg-[#0B2545]" />
              How it works
            </div>
            <h2 className="font-serif text-3xl md:text-7xl leading-[1.05] md:leading-[0.98] tracking-tight text-[#0B2545]">
              From <span className="italic text-[#0B2545]/70">tell&#8209;us</span> to <span className="italic text-[#0B2545]/70">turn&#8209;the&#8209;key</span> in three steps.
            </h2>
            <p className="mt-4 md:mt-8 text-[#5B6472] text-sm md:text-xl leading-relaxed max-w-2xl">
              Skip the tabs, the phone trees, and the surprise fees. We handle the comparison — you just tell us where and when.
            </p>
          </div>

          {/* Desktop grid */}
          <div className="hidden md:grid grid-cols-3 gap-6 relative">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="relative group h-full">
                  {i < STEPS.length - 1 && (
                    <div className="hidden md:flex absolute top-16 -right-3 z-10 items-center">
                      <div className="h-px w-6 bg-[#0B2545]/20" />
                      <ArrowRight className="h-4 w-4 text-[#0B2545]/40 -ml-1" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="bg-white border border-line p-8 md:p-10 h-full transition-all duration-500 group-hover:border-[#0B2545] group-hover:shadow-[0_20px_60px_-24px_rgba(11,37,69,0.25)] group-hover:-translate-y-1">
                    <div className="flex items-start justify-between mb-10">
                      <div className="font-serif text-7xl md:text-8xl leading-none text-[#0B2545] tracking-tight">
                        <span className="italic text-[#0B2545]/25">0</span>
                        <span>{i + 1}</span>
                      </div>
                      <div className="h-14 w-14 bg-[#0B2545] text-white flex items-center justify-center group-hover:bg-[#13366B] transition-colors duration-500">
                        <s.icon className="h-6 w-6" strokeWidth={1.5} />
                      </div>
                    </div>
                    <div className="h-px w-12 bg-[#0B2545]/20 mb-6" />
                    <h3 className="font-serif text-2xl md:text-3xl text-[#0B2545] mb-4 leading-tight">{s.t}</h3>
                    <p className="text-[#5B6472] leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Mobile horizontal snap carousel */}
          <div className="md:hidden -mx-5 px-5 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-3">
            {STEPS.map((s, i) => (
              <div
                key={i}
                className="snap-start shrink-0 w-[78%] bg-white border border-line p-5"
                data-testid={`how-step-mobile-${i}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="font-serif text-4xl leading-none text-[#0B2545] tracking-tight">
                    <span className="italic text-[#0B2545]/25">0</span>
                    <span>{i + 1}</span>
                  </div>
                  <div className="h-9 w-9 bg-[#0B2545] text-white flex items-center justify-center">
                    <s.icon className="h-4 w-4" strokeWidth={1.6} />
                  </div>
                </div>
                <div className="h-px w-8 bg-[#0B2545]/20 mb-3" />
                <h3 className="font-serif text-lg text-[#0B2545] mb-2 leading-tight">{s.t}</h3>
                <p className="text-[#5B6472] text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="md:hidden mt-2 flex items-center gap-1.5 text-[9px] font-mono tracking-wider uppercase text-[#5B6472]">
            <span>Swipe</span>
            <ArrowRight className="h-2.5 w-2.5" strokeWidth={2} />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-6 md:py-14 bg-[#F5F7FA] border-y border-line" data-testid="categories-section">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 md:mb-10 gap-4 md:gap-6">
            <div>
              <div className="overline mb-3 md:mb-4">Rental categories</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#0B2545]">
                Every class, compared.
              </h2>
              <p className="mt-2 md:mt-3 text-sm md:text-base text-[#5B6472] max-w-lg">
                From economy hatchbacks to full-size SUVs. Prices shown are starting-from estimates.
              </p>
            </div>
            <Link to="/categories" className="btn-ghost-light !hidden md:!inline-flex" data-testid="all-categories-link">
              View all categories <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>

          {/* Desktop grid */}
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {CATEGORIES.map((c, i) => (
              <CategoryCard key={c.slug} category={c} index={i} />
            ))}
          </div>

          {/* Mobile — auto-advancing 2x2 grid carousel */}
          <CategoryGridCarousel categories={CATEGORIES} />
        </div>
      </section>

      {/* Cities */}
      <section className="py-6 md:py-14 bg-white" data-testid="cities-section">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 md:mb-10 gap-4 md:gap-6">
            <div>
              <div className="overline mb-3 md:mb-4">Popular US cities</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#0B2545]">
                Ten cities. Every major airport.
              </h2>
              <p className="mt-2 md:mt-3 text-sm md:text-base text-[#5B6472] max-w-lg">
                The busiest US rental markets, compared side-by-side.
              </p>
            </div>
            <Link to="/cities" className="btn-ghost-light !hidden md:!inline-flex" data-testid="all-cities-link">
              View all cities <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>

          {/* Desktop grid */}
          <div className="hidden sm:grid grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {CITIES.map((c, i) => (
              <CityCard key={c.slug} city={c} index={i} />
            ))}
          </div>

          {/* Mobile — auto-advancing 2x2 grid carousel */}
          <CityGridCarousel cities={CITIES} />
        </div>
      </section>

      {/* Why us */}
      <section className="py-6 md:py-14 bg-[#0B2545] text-white" data-testid="why-section">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 mb-6 md:mb-10">
            <div className="md:col-span-5">
              <div className="overline-light mb-3 md:mb-4">Why Carztohire</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight text-white">
                A smarter way to book a rental car.
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-white/80 text-sm md:text-lg leading-relaxed max-w-2xl">
                Rental prices swing by class, city, provider, and driver age. We track them
                across every major US brand so you don't have to.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-8">
            {WHY.map((w, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="border border-white/15 p-4 md:p-8 h-full bg-[#0F2C55]/40">
                  <div className="h-9 w-9 md:h-10 md:w-10 border border-white/25 flex items-center justify-center mb-3 md:mb-6">
                    <w.icon className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                  <h3 className="font-serif text-base md:text-2xl text-white mb-2 md:mb-3 leading-tight">{w.t}</h3>
                  <p className="text-white/70 text-xs md:text-sm leading-relaxed">{w.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-6 md:py-14 bg-white" data-testid="testimonials-section">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="mb-6 md:mb-10">
            <div className="overline mb-3 md:mb-4">What travelers say</div>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#0B2545] max-w-2xl">
              Trusted by thousands of US travelers.
            </h2>
          </div>

          {/* Desktop grid */}
          <div className="hidden md:grid grid-cols-3 gap-6 md:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="card-flat p-7 md:p-8 h-full">
                  <div className="flex gap-0.5 mb-5">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className="h-3.5 w-3.5 fill-[#0B2545] text-[#0B2545]" strokeWidth={0} />
                    ))}
                  </div>
                  <blockquote className="text-[#0B2545] text-lg leading-relaxed mb-6">
                    "{t.q}"
                  </blockquote>
                  <figcaption className="text-sm text-[#5B6472]">
                    <span className="font-medium text-[#0B2545]">{t.name}</span>
                    <span className="mx-2">·</span>
                    <span>{t.city}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          {/* Mobile horizontal snap */}
          <div className="md:hidden -mx-5 px-5 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-3">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="snap-start shrink-0 w-[80%] card-flat p-5">
                <div className="flex gap-0.5 mb-3">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} className="h-3 w-3 fill-[#0B2545] text-[#0B2545]" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="text-[#0B2545] text-sm leading-relaxed mb-4">
                  "{t.q}"
                </blockquote>
                <figcaption className="text-xs text-[#5B6472]">
                  <span className="font-medium text-[#0B2545]">{t.name}</span>
                  <span className="mx-2">·</span>
                  <span>{t.city}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="py-8 md:py-14 bg-[#F5F7FA] border-t border-line" data-testid="faq-section">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">Frequently asked</div>
            <h2 className="font-serif text-4xl md:text-3xl leading-tight text-[#0B2545]">
              Straight answers.
            </h2>
            <Link to="/faq" className="btn-ghost-light mt-8" data-testid="all-faq-link">
              See all FAQs <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
          <div className="md:col-span-8">
            <FAQAccordion items={FAQS_GENERAL.slice(0, 5)} testIdPrefix="home-faq" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-8 md:py-20 bg-[#0B2545] text-white" data-testid="final-cta">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline-light mb-2 md:mb-4">Ready when you are</div>
            <h2 className="font-serif text-xl md:text-6xl leading-tight text-white">
              Call us for a free quote — every major provider compared in minutes.
            </h2>
          </div>
          <div className="md:col-span-4">
            <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-3">
              <a
                href={BRAND.phoneHref}
                className="btn-inverse justify-center whitespace-nowrap !py-2 md:!py-3.5 text-sm md:text-base"
                data-testid="final-call"
              >
                <Phone className="h-4 w-4" strokeWidth={2} />
                <span className="hidden sm:inline">{BRAND.phone}</span>
                <span className="sm:hidden">Call</span>
              </a>
              <div className="relative inline-flex flex-col group">
                <Link
                  to="/contact"
                  className="btn-ghost-dark justify-center whitespace-nowrap !py-2 md:!py-3.5 text-sm md:text-base"
                  data-testid="final-quote"
                >
                  <span className="hidden sm:inline">Submit an inquiry</span>
                  <span className="sm:hidden">Inquiry</span>
                  <ArrowRight className="h-4 w-4 hidden sm:inline" strokeWidth={1.8} />
                </Link>
                {/* Desktop tooltip only — mobile disclaimer spans both buttons below on a single line */}
                <TermsHint variant="dark" align="center" dataTestId="final-quote-hint" hideMobile />
              </div>
            </div>
            {/* Mobile-only single-line disclaimer below both buttons */}
            <p
              className="md:hidden mt-2 text-[10.5px] leading-snug text-white/60 whitespace-nowrap text-center"
              data-testid="final-quote-hint-mobile"
            >
              By clicking, you agree to our{" "}
              <Link to="/terms-conditions" className="text-white/85 underline underline-offset-2">
                Terms &amp; Conditions
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

const STEPS = [
  {
    icon: Search,
    t: "Tell us your trip",
    d: "Pickup city, dates, category, and lead driver age. Under 30 seconds — no login required.",
  },
  {
    icon: ClipboardList,
    t: "We compare across every major brand",
    d: "Alamo, Hertz, Sixt, Enterprise, Budget, Avis, National, Dollar, and Thrifty — side by side.",
  },
  {
    icon: Car,
    t: "You choose, we book",
    d: "Pick the option that suits you and we handle the reservation from start to finish.",
  },
];

const WHY = [
  { icon: Search, t: "Every major brand, one search", d: "Every national brand compared under a single inquiry — plus regional operators in select cities." },
  { icon: Check,  t: "Zero booking fees",       d: "We're compensated by provider partners — never by you. The quote is the price you pay." },
  { icon: Phone,  t: "Real phone support",       d: "Talk to a US-based team in Chattanooga, TN. No chatbots, no phone trees." },
  { icon: Car,    t: "US-wide coverage",         d: "Every major airport, every major downtown desk, all 50 states." },
];

const TESTIMONIALS = [
  { q: "Compared eight quotes in one call. Ended up in a Sixt convertible for less than my usual Enterprise sedan.", name: "Rachel M.", city: "Miami" },
  { q: "Carztohire found a minivan through Alamo when every other site said sold out. Booked in three minutes.",    name: "David K.",  city: "Orlando" },
  { q: "Great team on the phone. They explained why Hertz was cheaper than Sixt for my dates. Refreshingly honest.", name: "Amanda B.", city: "Las Vegas" },
];
